/**
 * 用戶上下文服務
 * 處理用戶當前上下文（專案和頁面）的獲取和設置
 */
import { api, endpoints } from "@/infrastructure/api";
import { secureStorage } from "@/infrastructure/storage";
import { STORAGE_KEYS } from "@/domain/constants/storage";

/**
 * 用戶建站階段枚舉
 */
export enum OnboardingStage {
  // 初始階段，尚未開始建站
  INITIAL = "initial",
  // 用戶輪廓收集階段，收集用戶基本資訊
  PROFILE_COLLECTION = "profile_collection",
  // 需求調研階段，收集用戶對網站的需求
  REQUIREMENTS_RESEARCH = "requirements_research",
  // 建站階段，正在進行建站
  BUILDING = "building",
  // 完成階段，建站已完成
  COMPLETED = "completed",
}

/**
 * 用戶上下文介面
 * 注意：只有 currentProjectId 和 currentPageId 可以通過 API 直接更改
 * 其他屬性由系統內部管理，不能通過 setCurrentContext API 更改
 */
export interface UserContext {
  current_project_id?: string; // 當前專案 ID (可通過 API 更改)
  current_page_id?: string; // 當前頁面 ID (可通過 API 更改)
  last_visited?: string; // 最後訪問時間 (系統內部管理)
  is_new_user?: boolean; // 是否為新用戶 (系統內部管理)
  onboarding_completed?: boolean; // 是否完成建站引導 (系統內部管理)
  profile_collected?: boolean; // 是否已收集用戶輪廓 (系統內部管理)
  requirements_collected?: boolean; // 是否已收集需求 (系統內部管理)
}

/**
 * 用戶上下文服務
 * 提供獲取和設置用戶當前上下文的方法
 */
export const contextService = {
  /**
   * 獲取用戶當前上下文
   * @returns 用戶上下文
   */
  async getCurrentContext(): Promise<UserContext> {
    try {
      console.log("[contextService] 調用 API 獲取用戶上下文");
      const context = await api.get<UserContext>(endpoints.users.context);
      console.log("[contextService] 成功獲取用戶上下文", context);
      return context;
    } catch (error) {
      console.error("[contextService] Failed to get user context:", error);
      // 如果 API 調用失敗，返回空的上下文
      return {};
    }
  },

  /**
   * 設置用戶當前上下文
   * 注意：只有 currentProjectId 和 currentPageId 可以通過此 API 更改
   * 其他屬性將被忽略，由系統內部管理
   * @param context 用戶上下文 (只有 currentProjectId 和 currentPageId 會被處理)
   * @returns 更新後的用戶上下文
   */
  async setCurrentContext(context: Partial<UserContext>): Promise<UserContext> {
    try {
      // 提取所有可更改的屬性
      const allowedContext: Partial<UserContext> = {
        current_project_id: context.current_project_id,
        current_page_id: context.current_page_id,
        is_new_user: context.is_new_user,
        onboarding_completed: context.onboarding_completed,
        profile_collected: context.profile_collected,
        requirements_collected: context.requirements_collected,
      };

      return await api.post<UserContext>(
        endpoints.users.setContext,
        allowedContext,
      );
    } catch (error) {
      console.error("Failed to set user context:", error);
      // 如果 API 調用失敗，返回原始上下文 (僅包含允許的屬性)
      return {
        current_project_id: context.current_project_id,
        current_page_id: context.current_page_id,
      } as UserContext;
    }
  },

  /**
   * 從安全儲存獲取用戶上下文
   * 作為 API 調用失敗時的備用方案
   * @returns 用戶上下文
   */
  getLocalContext(): UserContext {
    try {
      const localContext = secureStorage.getItem(STORAGE_KEYS.USER_CONTEXT);
      return localContext
        ? typeof localContext === "object"
          ? localContext
          : JSON.parse(localContext as string)
        : {};
    } catch (error) {
      console.error("Failed to get local context:", error);
      return {};
    }
  },

  /**
   * 將用戶上下文保存到安全儲存
   * @param context 用戶上下文
   */
  saveLocalContext(context: UserContext): void {
    try {
      secureStorage.setItem(STORAGE_KEYS.USER_CONTEXT, context);
    } catch (error) {
      console.error("Failed to save local context:", error);
    }
  },

  /**
   * 獲取用戶上下文（優先使用 localStorage，如果沒有才使用 API）
   * @returns 用戶上下文
   */
  async getContext(): Promise<UserContext> {
    try {
      console.log("[contextService] getContext: 開始獲取用戶上下文");

      // 先嘗試從 localStorage 獲取上下文
      const localContext = this.getLocalContext();
      console.log(
        "[contextService] getContext: 從 localStorage 獲取的上下文",
        localContext,
      );

      // 如果本地儲存有有效的上下文，直接返回
      if (localContext && Object.keys(localContext).length > 0) {
        console.log("[contextService] getContext: 使用本地儲存的上下文");
        return localContext;
      }

      // 如果本地儲存沒有有效的上下文，才嘗試從 API 獲取
      console.log(
        "[contextService] getContext: 本地儲存沒有有效的上下文，嘗試從 API 獲取",
      );
      const apiContext = await this.getCurrentContext();

      // 如果 API 獲取成功且有內容，保存到 localStorage 並返回
      if (apiContext && Object.keys(apiContext).length > 0) {
        console.log(
          "[contextService] getContext: API 返回有效上下文，保存到本地儲存",
        );
        this.saveLocalContext(apiContext);
        return apiContext;
      }

      // 如果 API 也沒有返回有效的上下文，返回空對象
      console.log(
        "[contextService] getContext: API 也沒有返回有效的上下文，返回空對象",
      );
      return {};
    } catch (error) {
      // 如果 API 調用失敗，從 localStorage 獲取
      console.error(
        "[contextService] getContext: Failed to get context from API:",
        error,
      );
      return this.getLocalContext();
    }
  },
};
